/**
 * ImportPreviewTable
 *
 * Paginated preview table for bulk user import.
 * Shows all parsed rows (valid + invalid) with status badges,
 * error details, and a summary banner.
 */

import React from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export interface ParsedUserRow {
  rowIndex: number;
  email: string;
  firstName: string;
  lastName: string;
  role: string;
  externalId?: string;
  isValid: boolean;
  errors: string[];
}

interface ImportPreviewTableProps {
  rows: ParsedUserRow[];
  page: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  importMode: 'abort_on_error' | 'continue_on_error';
}

const ROLE_LABELS: Record<string, string> = {
  admin: 'Admin',
  operator: 'Operator',
  staff: 'Staff',
};

const ROLE_COLORS: Record<string, string> = {
  admin: 'bg-purple-100 text-purple-800',
  operator: 'bg-blue-100 text-blue-800',
  staff: 'bg-gray-100 text-gray-700',
};

export function ImportPreviewTable({
  rows,
  page,
  pageSize,
  onPageChange,
  importMode,
}: ImportPreviewTableProps) {
  const totalRows = rows.length;
  const validCount = rows.filter((r) => r.isValid).length;
  const invalidCount = totalRows - validCount;
  const totalPages = Math.max(1, Math.ceil(totalRows / pageSize));

  const pageRows = rows.slice((page - 1) * pageSize, page * pageSize);

  return (
    <div className="space-y-3">
      {/* Summary banner */}
      <div className="flex items-center justify-between px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg text-sm">
        <span className="text-gray-700">
          <span className="font-medium text-green-700">{validCount} valid</span>
          {' / '}
          <span className={`font-medium ${invalidCount > 0 ? 'text-red-700' : 'text-gray-500'}`}>
            {invalidCount} invalid
          </span>
          {' users'}
        </span>
        {totalPages > 1 && (
          <span className="text-gray-500">
            Page {page} of {totalPages}
          </span>
        )}
      </div>

      {/* Abort-on-error warning */}
      {importMode === 'abort_on_error' && invalidCount > 0 && (
        <div className="flex items-start gap-2 px-3 py-2 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
          <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
          <span>
            Import will be aborted because errors exist. Switch to{' '}
            <span className="font-medium">"Continue on error"</span> to skip invalid rows.
          </span>
        </div>
      )}

      {/* Table */}
      <div className="border rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600 w-10">#</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Email</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">First Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Last Name</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Role</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">External ID</th>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Status</th>
              </tr>
            </thead>
            <tbody>
              {pageRows.map((row) => (
                <React.Fragment key={row.rowIndex}>
                  <tr className={`border-t ${row.isValid ? '' : 'bg-red-50'}`}>
                    <td className="px-3 py-2 text-gray-500">{row.rowIndex}</td>
                    <td className="px-3 py-2 font-mono text-xs">{row.email || '—'}</td>
                    <td className="px-3 py-2">{row.firstName || '—'}</td>
                    <td className="px-3 py-2">{row.lastName || '—'}</td>
                    <td className="px-3 py-2">
                      {row.role ? (
                        <span
                          className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                            ROLE_COLORS[row.role] ?? 'bg-gray-100 text-gray-700'
                          }`}
                        >
                          {ROLE_LABELS[row.role] ?? row.role}
                        </span>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-gray-500">{row.externalId || '—'}</td>
                    <td className="px-3 py-2">
                      {row.isValid ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Valid
                        </span>
                      ) : (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
                          Error
                        </span>
                      )}
                    </td>
                  </tr>
                  {!row.isValid && row.errors.length > 0 && (
                    <tr className="bg-red-50 border-t border-red-100">
                      <td />
                      <td colSpan={6} className="px-3 pb-2">
                        <ul className="list-disc list-inside text-xs text-red-600 space-y-0.5">
                          {row.errors.map((err, i) => (
                            <li key={i}>{err}</li>
                          ))}
                        </ul>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              {pageRows.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-3 py-6 text-center text-gray-400 text-sm">
                    No rows to display
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            className="flex items-center gap-1"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <span className="text-sm text-gray-600">
            {(page - 1) * pageSize + 1}–{Math.min(page * pageSize, totalRows)} of {totalRows}
          </span>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            className="flex items-center gap-1"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
}
